/**
 * Admin V3 — Bookings — Unassigned queue list
 *
 * Card-per-booking (mirrors the legacy V1 "Courses à assigner" panel):
 *   date/time · pickup → destination · distance · price, + dispatch controls.
 *
 * Read display is unconditional; the dispatch actions (BookingDispatchControls)
 * HIDE-gate themselves per RBAC permission. Loading = skeleton, error = banner +
 * retry, empty = "Toutes les courses sont assignées".
 */

import React from 'react';
import { Calendar, MapPin, UserCheck, AlertCircle, RotateCcw } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { EmptyState } from '../../shared/visualTokens';
import { t } from '../../i18n';
import BookingDispatchControls from './BookingDispatchControls';

const trunc = (s, n = 42) => {
  if (!s) return '—';
  return s.length <= n ? s : `${s.slice(0, n)}…`;
};

const SkeletonCards = () => (
  <div className="space-y-3" data-testid="adminv3-unassigned-skeleton">
    {Array(3).fill(0).map((_, i) => (
      <div key={`sk-${i}`} className="rounded-2xl border border-slate-200 bg-white p-4">
        <div className="h-3 w-40 rounded bg-slate-100 animate-pulse mb-2" />
        <div className="h-3 w-full max-w-md rounded bg-slate-100 animate-pulse" />
      </div>
    ))}
  </div>
);

const UnassignedQueueTable = ({ bookings, availableDrivers, loading, error, onRetry, onDone, locale = 'fr' }) => {
  if (error) {
    return (
      <div className="rounded-xl border border-rose-200 bg-rose-50 p-4" data-testid="adminv3-unassigned-error">
        <div className="flex items-start gap-3">
          <AlertCircle className="h-5 w-5 text-rose-500 mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <p className="text-sm font-semibold text-rose-700">{t('dispatch.error.title', locale)}</p>
            <p className="text-xs text-rose-600 mt-1 break-words">{String(error)}</p>
          </div>
          <Button
            variant="outline" size="sm" onClick={onRetry}
            className="rounded-lg border-rose-200 bg-white text-rose-700 hover:bg-rose-50"
            data-testid="adminv3-unassigned-retry"
          >
            <RotateCcw className="h-3.5 w-3.5 mr-1.5" />
            {t('dispatch.error.retry', locale)}
          </Button>
        </div>
      </div>
    );
  }

  if (loading) return <SkeletonCards />;

  if (!bookings || bookings.length === 0) {
    return (
      <div data-testid="adminv3-unassigned-empty">
        <EmptyState icon={UserCheck} title={t('dispatch.empty.title', locale)} />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="adminv3-unassigned-list">
      {bookings.map((b) => (
        <div
          key={b.id}
          className="rounded-2xl border border-slate-200 bg-white p-4"
          data-testid={`adminv3-unassigned-row-${b.id}`}
        >
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-2 text-sm font-semibold text-slate-900">
                <Calendar className="h-4 w-4 text-slate-400" />
                {b.date || '—'}{b.time ? ` à ${b.time}` : ''}
              </div>
              <div className="flex items-start gap-2 text-sm text-slate-600">
                <MapPin className="mt-0.5 h-4 w-4 text-emerald-500 flex-shrink-0" />
                {trunc(b.pickup_address)}
              </div>
              <div className="flex items-start gap-2 text-sm text-slate-500 mt-1">
                <MapPin className="mt-0.5 h-4 w-4 text-rose-500 flex-shrink-0" />
                {trunc(b.destination_address)}
              </div>
              <div className="flex gap-4 mt-2 text-xs text-slate-400">
                <span>{b.distance_km != null ? `${b.distance_km} km` : '—'}</span>
                <span>{b.estimated_price != null ? `${b.estimated_price} €` : '—'}</span>
              </div>
            </div>
            <div className="flex-shrink-0">
              <BookingDispatchControls
                booking={b}
                availableDrivers={availableDrivers}
                onDone={onDone}
                locale={locale}
              />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default UnassignedQueueTable;
